import * as React from "react";
import { StreamingProvider, type StreamingClient } from "@authe/streaming";
import { sessionIdentityKey, tokenMatchesSession, type Session, type SessionManager } from "@authe/fe-commons/session";
import { useSession } from "@authe/ui-core/session";
import { createDashboardStreamingClient } from "./streaming";

/** Own the socket for exactly one authenticated identity, including impersonation. */
export function DashboardStreamingProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session, isAuthenticated, manager } = useSession();
  if (!isAuthenticated || !session) return <>{children}</>;
  const scope = sessionIdentityKey(session);
  return <AuthenticatedStreaming key={scope} manager={manager} session={session} scope={scope}>{children}</AuthenticatedStreaming>;
}

function AuthenticatedStreaming({ children, manager, session, scope }: {
  children: React.ReactNode;
  manager: SessionManager;
  session: Session;
  scope: string;
}): React.ReactElement {
  const [client, setClient] = React.useState<StreamingClient | null>(null);
  const expected = React.useRef(session);
  React.useEffect(() => {
    let active = true;
    let ended = false;
    let current: StreamingClient | null = null;
    const pause = () => {
      current?.stop();
      current = null;
      if (active) setClient(null);
    };
    const end = () => { ended = true; pause(); };
    const start = () => {
      if (!active || ended || current) return;
      const created = createDashboardStreamingClient(async () => {
        const token = await manager.getAccessToken();
        if (!active || ended || created !== current) throw new Error("The streaming session ended");
        if (!token) {
          pause();
          throw new Error("The streaming token is unavailable");
        }
        if (!tokenMatchesSession(token, expected.current)) {
          end();
          throw new Error("The streaming session changed identity");
        }
        return token;
      }, scope);
      current = created;
      setClient(created);
    };
    const off = manager.subscribeIdentityChange(end);
    // A temporary refresh failure pauses delivery, then recovers with the same
    // identity. A logout or a foreign token ends this identity permanently.
    const check = async () => {
      const token = await manager.getAccessToken();
      if (!active || ended) return;
      if (!token) pause();
      else if (!tokenMatchesSession(token, expected.current)) end();
      else start();
    };
    start();
    const timer = setInterval(() => { void check(); }, 30_000);
    const storage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith("@@auth0spajs@@")) void check();
    };
    window.addEventListener("storage", storage);
    return () => {
      active = false;
      ended = true;
      off();
      clearInterval(timer);
      window.removeEventListener("storage", storage);
      current?.stop();
    };
  }, [manager, scope]);

  if (!client) return <>{children}</>;
  return <StreamingProvider client={client} stopOnUnmount>{children}</StreamingProvider>;
}
